import { cloneDeep, some } from 'lodash-es'
import { assign, setup } from 'xstate'

interface FormField {
  error: string
  value: string
}

export interface GiftCardContext {
  amount: { label: string; value: number }
  customAmount: { error: string; value: number | '' }
  info: {
    giftMessage: FormField
    recipientEmail: FormField
  }
}

export type GiftCardEvents =
  | {
      amount: { label: string; value: number }
      type: 'AMOUNT_CHOSEN'
    }
  | { type: 'BACK' }
  | { type: 'CONTINUE' }
  | { amount: number | ''; type: 'CUSTOM_AMOUNT_CHANGED' }
  | {
      fieldName: keyof GiftCardContext['info']
      type: 'FIELD_ENTERED'
      value: string
    }
  | { type: 'GIFT_CARD_PURCHASED' }

export const AMOUNT_OPTIONS = [
  { label: '$75', value: 75 },
  { label: '$100', value: 100 },
  { label: '$125', value: 125 },
  { label: '$150', value: 150 },
  { label: '$200', value: 200 },
  { label: '$250', value: 250 },
  { label: '$350', value: 350 },
  { label: '$500', value: 500 },
  { label: 'Custom Amount', value: 0 },
]
const DEFAULT_AMOUNT = AMOUNT_OPTIONS[1]

const INITIAL_CONTEXT = {
  amount: DEFAULT_AMOUNT,
  customAmount: { error: '', value: '' },
  info: {
    giftMessage: { error: '', value: 'Enjoy your gift!' },
    recipientEmail: { error: '', value: '' },
  },
} satisfies GiftCardContext

export const giftCardMachine = setup({
  types: {} as {
    context: GiftCardContext
    events: GiftCardEvents
  },
  actions: {
    clearInfo: assign(({ context }) => {
      return { ...context, info: INITIAL_CONTEXT.info }
    }),
    storeAmount: assign({
      amount: (_, params: { amount: GiftCardContext['amount'] }) =>
        params.amount,
    }),
    storeCustomAmount: assign({
      customAmount: (
        _,
        params: { amount: GiftCardContext['customAmount']['value'] }
      ) => {
        return { error: '', value: params.amount }
      },
    }),
    storeCustomAmountError: assign({
      customAmount: ({ context }) => {
        return {
          ...context.customAmount,
          error: getCustomAmountError({
            amount: context.amount.value,
            customAmount: context.customAmount.value,
          }),
        }
      },
    }),
    storeInfo: assign(
      (
        { context },
        params: { fieldName: keyof GiftCardContext['info']; value: string }
      ) => {
        return {
          ...context,
          info: {
            ...context.info,
            [params.fieldName]: { error: '', value: params.value },
          },
        }
      }
    ),
    storeInfoErrors: assign(({ context }) => {
      const newCtx = cloneDeep(context)
      const errors = getInfoErrors(context.info)

      newCtx.info.giftMessage.error = errors.giftMessage
      newCtx.info.recipientEmail.error = errors.recipientEmail

      return newCtx
    }),
  },
  guards: {
    hasCustomAmountError: ({ context }) => {
      return !!getCustomAmountError({
        amount: context.amount.value,
        customAmount: context.customAmount.value,
      })
    },
    hasInfoErrors: ({ context }) => {
      return some(getInfoErrors(context.info), (error) => !!error)
    },
  },
}).createMachine({
  id: 'giftCard',
  context: INITIAL_CONTEXT,
  initial: 'choosingAmount',
  states: {
    choosingAmount: {
      on: {
        AMOUNT_CHOSEN: {
          actions: [
            {
              type: 'storeAmount',
              params: ({ event }) => ({ amount: event.amount }),
            },
          ],
        },
        CONTINUE: [
          {
            guard: 'hasCustomAmountError',
            actions: ['storeCustomAmountError'],
          },
          { target: 'enteringInfo' },
        ],
        CUSTOM_AMOUNT_CHANGED: {
          actions: [
            {
              type: 'storeCustomAmount',
              params: ({ event }) => ({ amount: event.amount }),
            },
          ],
        },
      },
    },
    enteringInfo: {
      on: {
        BACK: { actions: ['clearInfo'], target: 'choosingAmount' },
        CONTINUE: [
          {
            guard: 'hasInfoErrors',
            actions: ['storeInfoErrors'],
          },
          { target: 'enteringPayment' },
        ],
        FIELD_ENTERED: {
          actions: [
            {
              type: 'storeInfo',
              params: ({ event }) => ({
                fieldName: event.fieldName,
                value: event.value,
              }),
            },
          ],
        },
      },
    },
    enteringPayment: {
      on: {
        BACK: { target: 'enteringInfo' },
        GIFT_CARD_PURCHASED: { target: 'purchased' },
      },
    },
    purchased: { type: 'final' },
  },
})

function getCustomAmountError({
  amount,
  customAmount,
}: {
  amount: number
  customAmount: number | ''
}) {
  if (amount > 0) {
    return ''
  }

  if (customAmount === '') {
    return 'Please enter a custom amount.'
  } else if (!Number.isInteger(customAmount)) {
    return 'Please enter a whole dollar amount without cents.'
  } else if (customAmount <= 0 || customAmount >= 1001) {
    return 'Please enter a custom amount between $1 and $1,000.'
  }

  return ''
}

function getInfoErrors(formData: GiftCardContext['info']) {
  return {
    giftMessage:
      formData.giftMessage.value === '' ? 'Please enter a gift message.' : '',
    recipientEmail:
      formData.recipientEmail.value === '' ? 'Please enter an email.' : '',
  }
}
